// Rendus de l'agenda d'un dossier : liste par mois, grille du mois, petit
// calendrier. Purement visuels : les données et les clics viennent de l'onglet.
import { useMemo, useState } from 'react';
import {
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '../../lib/cn';
import type {
  DossierCalendarEvent,
  DossierCalendarEventKind,
} from '../../services/calendar/dossierCalendar';

export const AGENDA_KIND_DOT: Record<DossierCalendarEventKind, string> = {
  hearing: 'bg-[#ea7549]',
  appointment: 'bg-cime-gold',
  task: 'bg-cime-primary',
  prescription: 'bg-red-600',
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const timeOf = (event: DossierCalendarEvent) =>
  event.allDay || !event.start.includes('T') ? null : format(new Date(event.start), 'HH:mm');

interface RowHandlers {
  onEventClick: (event: DossierCalendarEvent) => void;
  onEnter: (id: string, el: HTMLElement) => void;
  onLeave: () => void;
}

// ─── Liste ───────────────────────────────────────────────────────────────────

export function AgendaList({
  events,
  now,
  highlightId,
  ...handlers
}: {
  events: DossierCalendarEvent[];
  now: Date;
  /** Événement à mettre en avant (lien direct vers une audience). */
  highlightId?: string | null;
} & RowHandlers) {
  const [pastOpen, setPastOpen] = useState(false);
  const today = startOfDay(now);

  const { overdue, months, past } = useMemo(() => {
    const sorted = [...events].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    const before = sorted.filter((e) => new Date(e.start) < today);
    const upcoming = sorted.filter((e) => new Date(e.start) >= today);
    const byMonth = new Map<string, DossierCalendarEvent[]>();
    for (const event of upcoming) {
      const key = format(new Date(event.start), 'yyyy-MM');
      byMonth.set(key, [...(byMonth.get(key) ?? []), event]);
    }
    return {
      // Une tâche ouverte dont la date est passée reste à faire : elle est en retard, pas « passée ».
      overdue: before.filter((e) => e.kind === 'task'),
      past: before.filter((e) => e.kind !== 'task').reverse(),
      months: [...byMonth.entries()],
    };
  }, [events, today]);

  const row = (event: DossierCalendarEvent, tone?: 'late' | 'past') => (
    <AgendaRow
      key={event.id}
      event={event}
      now={now}
      tone={tone}
      highlighted={highlightId != null && (event.hearingId === highlightId || event.id === `hearing:${highlightId}`)}
      {...handlers}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {overdue.length > 0 ? (
        <section>
          <h4 className="mb-1.5 px-1 text-xs font-semibold text-[#c8431f]">
            En retard <span className="font-normal tabular-nums">{overdue.length}</span>
          </h4>
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
            {overdue.map((event) => row(event, 'late'))}
          </div>
        </section>
      ) : null}

      <div className="flex items-center gap-3 text-xs font-medium text-cime-gold-text">
        <span>Aujourd'hui · {format(now, 'EEE d MMM', { locale: fr })}</span>
        <span className="h-px flex-1 bg-cime-gold/40" />
      </div>

      {months.length === 0 ? (
        <p className="rounded-xl border border-dashed border-neutral-200 bg-white px-4 py-8 text-center text-[13px] text-neutral-400">
          Aucune échéance à venir. Les tâches datées, les audiences et les rendez-vous de ce dossier apparaîtront ici.
        </p>
      ) : (
        months.map(([key, items]) => (
          <section key={key}>
            <h4
              className="mb-1.5 px-1 text-[15px] font-semibold text-cime-text-primary"
            >
              {capitalize(format(new Date(`${key}-01T00:00:00`), 'MMMM yyyy', { locale: fr }))}
              <span className="ml-2 font-sans text-xs font-normal tabular-nums text-neutral-400">{items.length}</span>
            </h4>
            <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
              {items.map((event) => row(event))}
            </div>
          </section>
        ))
      )}

      {past.length > 0 ? (
        <section>
          <button
            type="button"
            onClick={() => setPastOpen((open) => !open)}
            aria-expanded={pastOpen}
            className="flex w-full items-center gap-2 px-1 py-1 text-left text-xs text-cime-text-muted hover:text-cime-text-primary"
          >
            <span>
              {past.length} événement{past.length > 1 ? 's' : ''} passé{past.length > 1 ? 's' : ''}
            </span>
            <span className="ml-auto font-semibold text-cime-text-primary">{pastOpen ? 'Masquer' : 'Afficher'}</span>
          </button>
          {pastOpen ? (
            <div className="mt-1.5 overflow-hidden rounded-xl border border-neutral-200 bg-white">
              {past.map((event) => row(event, 'past'))}
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function AgendaRow({
  event,
  now,
  tone,
  highlighted,
  onEventClick,
  onEnter,
  onLeave,
}: {
  event: DossierCalendarEvent;
  now: Date;
  tone?: 'late' | 'past';
  highlighted: boolean;
} & RowHandlers) {
  const start = new Date(event.start);
  const days = differenceInCalendarDays(start, now);
  const time = timeOf(event);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onEventClick(event)}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onEventClick(event);
      }}
      onMouseEnter={(e) => onEnter(event.id, e.currentTarget)}
      onMouseLeave={onLeave}
      ref={highlighted ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}
      className={cn(
        'flex h-[42px] cursor-pointer items-center gap-3 border-b border-neutral-100 px-3.5 outline-none transition-colors last:border-b-0 focus-visible:bg-neutral-50',
        highlighted ? 'bg-cime-gold/15 hover:bg-cime-gold/20' : 'hover:bg-neutral-50',
      )}
    >
      <span className="flex w-[62px] flex-shrink-0 items-baseline gap-1.5">
        <b className={cn('text-[15px] font-semibold tabular-nums', tone === 'late' ? 'text-[#c8431f]' : 'text-cime-text-primary')}>
          {format(start, 'd')}
        </b>
        <small className="text-[11px] text-neutral-400">
          {tone ? format(start, 'MMM', { locale: fr }) : format(start, 'EEE', { locale: fr })}
        </small>
      </span>
      <i aria-hidden className={cn('h-[7px] w-[7px] flex-shrink-0 rounded-full', AGENDA_KIND_DOT[event.kind])} />
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-sm font-medium',
          tone === 'past' ? 'text-cime-text-muted' : 'text-cime-text-primary',
        )}
      >
        {event.title}
      </span>
      {event.kind === 'hearing' && !tone && days >= 0 ? (
        <span className="flex-shrink-0 rounded-full bg-[#fdece6] px-2 py-0.5 text-[11px] font-bold tabular-nums text-[#c8431f]">
          {days === 0 ? "Aujourd'hui" : `J-${days}`}
        </span>
      ) : null}
      <span className="w-11 flex-shrink-0 text-right text-[13px] tabular-nums text-cime-text-muted">{time ?? ''}</span>
    </div>
  );
}

// ─── Mois ────────────────────────────────────────────────────────────────────

function monthDays(month: Date): Date[] {
  return eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
  });
}

const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function MonthNav({ month, onChange, small }: { month: Date; onChange: (next: Date) => void; small?: boolean }) {
  const arrow = 'grid h-7 w-7 place-items-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-cime-text-primary';
  return (
    <div className="flex items-center justify-between">
      <button type="button" className={arrow} onClick={() => onChange(addMonths(month, -1))} aria-label="Mois précédent">
        ‹
      </button>
      <b
        className={cn('font-semibold text-cime-text-primary', small ? 'text-sm' : 'text-base')}
      >
        {capitalize(format(month, 'MMMM yyyy', { locale: fr }))}
      </b>
      <button type="button" className={arrow} onClick={() => onChange(addMonths(month, 1))} aria-label="Mois suivant">
        ›
      </button>
    </div>
  );
}

export function AgendaMonth({
  events,
  now,
  month,
  onMonthChange,
  onDay,
  ...handlers
}: {
  events: DossierCalendarEvent[];
  now: Date;
  month: Date;
  onMonthChange: (next: Date) => void;
  /** Clic sur une case vide : création rapide ce jour-là. */
  onDay?: (day: string, x: number, y: number) => void;
} & RowHandlers) {
  const days = useMemo(() => monthDays(month), [month]);
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3">
      <MonthNav month={month} onChange={onMonthChange} />
      <div className="mt-2 grid grid-cols-7 text-center text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
          <span key={d} className="py-1.5">{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 border-l border-t border-neutral-100">
        {days.map((day) => {
          const items = events
            .filter((e) => isSameDay(new Date(e.start), day))
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
          const inMonth = isSameMonth(day, month);
          const today = isSameDay(day, now);
          return (
            <div
              key={day.toISOString()}
              title={onDay ? 'Cliquer pour ajouter un événement' : undefined}
              onClick={(e) => {
                if (!onDay || (e.target as HTMLElement).closest('button')) return;
                onDay(format(day, 'yyyy-MM-dd'), e.clientX, e.clientY);
              }}
              className={cn(
                'min-h-[92px] border-b border-r border-neutral-100 p-1.5',
                !inMonth && 'bg-[#fbfaf6]',
                onDay && 'cursor-cell hover:bg-[#fbfaf6]',
              )}
            >
              <span
                className={cn(
                  'mb-1 grid h-5 w-5 place-items-center rounded-full text-xs tabular-nums',
                  today ? 'bg-cime-primary font-semibold text-white' : inMonth ? 'text-cime-text-primary' : 'text-neutral-300',
                )}
              >
                {format(day, 'd')}
              </span>
              <div className="flex flex-col gap-0.5">
                {items.slice(0, 3).map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => handlers.onEventClick(event)}
                    onMouseEnter={(e) => handlers.onEnter(event.id, e.currentTarget)}
                    onMouseLeave={handlers.onLeave}
                    className="flex items-center gap-1.5 rounded px-1 py-0.5 text-left text-xs text-cime-text-primary hover:bg-neutral-100"
                  >
                    <i aria-hidden className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', AGENDA_KIND_DOT[event.kind])} />
                    {timeOf(event) ? <b className="font-semibold tabular-nums">{timeOf(event)}</b> : null}
                    <span className="truncate">{event.title}</span>
                  </button>
                ))}
                {items.length > 3 ? <span className="px-1 text-[11px] text-neutral-400">+{items.length - 3}</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Petit calendrier ────────────────────────────────────────────────────────

export function AgendaMiniCalendar({
  events,
  now,
  month,
  onMonthChange,
}: {
  events: DossierCalendarEvent[];
  now: Date;
  month: Date;
  onMonthChange: (next: Date) => void;
}) {
  const days = useMemo(() => monthDays(month), [month]);
  return (
    <aside className="rounded-xl border border-neutral-200 bg-white p-3.5">
      <MonthNav month={month} onChange={onMonthChange} small />
      <div className="mt-2 grid grid-cols-7 text-center text-[10px] font-bold uppercase text-neutral-400">
        {WEEKDAYS.map((d, i) => (
          <span key={i} className="py-1">{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5 text-center">
        {days.map((day) => {
          const kinds = [...new Set(events.filter((e) => isSameDay(new Date(e.start), day)).map((e) => e.kind))];
          const inMonth = isSameMonth(day, month);
          const today = isSameDay(day, now);
          return (
            <span key={day.toISOString()} className="relative grid h-[30px] place-items-center">
              <span
                className={cn(
                  'grid h-6 w-6 place-items-center rounded-md text-xs tabular-nums',
                  today
                    ? 'bg-cime-primary font-semibold text-white'
                    : kinds.includes('hearing')
                      ? 'bg-[#fdece6] font-semibold text-[#c8431f]'
                      : inMonth
                        ? 'text-cime-text-primary'
                        : 'text-neutral-300',
                )}
              >
                {format(day, 'd')}
              </span>
              {kinds.length > 0 ? (
                <span className="absolute bottom-0 flex gap-0.5">
                  {kinds.slice(0, 3).map((kind) => (
                    <i key={kind} className={cn('h-[3px] w-[3px] rounded-full', AGENDA_KIND_DOT[kind])} />
                  ))}
                </span>
              ) : null}
            </span>
          );
        })}
      </div>
    </aside>
  );
}
